import { useMsal } from '@azure/msal-react';

import React from 'react';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  Grid,
  Link,
  Button,
  Checkbox,
  Typography,
  CardHeader,
  CardContent,
  CardActions,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';

import { SnakebarToaster, Asterisk, TextField, AutoCompleteSingleSelect } from '@/components';
import { useCountriesReferentialOptions } from '@/features/borderCrossingRORO/shared/hooks';
import { loginRequest } from '@/lib/react-msal/authConfig';
import { SendPlaneLineIcon } from '@/utils/iconSVG';

import {
  stylesSubscribeCard,
  stylesSubscribeCardContent,
  stylesSubscribeFormSubtitle,
  stylesSubscribeCardSubheader,
  stylesAutoCompleteSingleSelect,
} from './SubscribePublicForm.styles';
import { useSubscribeForm } from './hooks';
import { REGISTER_SUBSCRIBE_FORM } from './types';

export const SubscribePublicForm: React.FunctionComponent = () => {
  const { t } = useTranslation('borderCrossingRORO_onBoarding');
  const {
    isSubscribeFormInError,
    formState: { errors },
    isSubscribePending,
    isSubscribeSuccess,
    control,
    register,
    handleCloseToaster,
    handleSubmitSubscribe,
    handleCancelTheRequest,
  } = useSubscribeForm();

  const { countriesOptions, isGetCountriesCodeLoading } = useCountriesReferentialOptions();

  const { instance } = useMsal();
  if (isGetCountriesCodeLoading) {
    return null;
  }

  const login = () => {
    instance.loginRedirect(loginRequest).catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });
  };

  if (isSubscribeSuccess) {
    return (
      <Box
        component='span'
        m='auto'
        height='75vw'
        display='flex'
        flexDirection='column'
        justifyContent='center'
        alignItems='center'
        gap={4}
      >
        <Typography variant='bodyXLBold'>
          {t('borderCrossingRORO.onBoarding.submitSubscribe.success.message')}
        </Typography>
        <Button onClick={login} variant='contained' sx={{ fontSize: 'bodyXLBold.fontSize', maxWidth: '15rem' }}>
          {t('borderCrossingRORO.onBoarding.submitSubscribe.success.loginButton.label')}
        </Button>
      </Box>
    );
  }
  return (
    <Card sx={stylesSubscribeCard}>
      <CardHeader
        title={
          <Typography variant='h1' color='primary.dark2'>
            {t('borderCrossingRORO.onBoarding.subscribe.form.label')}
          </Typography>
        }
        subheader={
          <Typography variant='h2' color='primary.dark2' sx={stylesSubscribeCardSubheader}>
            {t('borderCrossingRORO.onBoarding.subscribe.form.customsProENS.label')}
          </Typography>
        }
        disableTypography
      />
      <CardContent sx={stylesSubscribeCardContent}>
        <Typography component='div' variant='bodyMDMedium' sx={stylesSubscribeFormSubtitle}>
          {t('borderCrossingRORO.onBoarding.subscribe.form.generalInformation.label')}
        </Typography>
        <Grid container spacing={2} item xs={6} pb={3}>
          <Grid item xs={12}>
            <TextField
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              error={errors.name}
              id={REGISTER_SUBSCRIBE_FORM.name}
              registration={register(REGISTER_SUBSCRIBE_FORM.name)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.name.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <TextField
              fullWidth
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.city}
              id={REGISTER_SUBSCRIBE_FORM.city}
              registration={register(REGISTER_SUBSCRIBE_FORM.city)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.city.label')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6}>
            <Controller
              control={control}
              name={REGISTER_SUBSCRIBE_FORM.countryCodeId}
              render={({ field: { value: prevValue, onChange } }) => (
                <AutoCompleteSingleSelect
                  isRequired
                  multiple={false}
                  labelSx={{ pb: 1 }}
                  options={countriesOptions}
                  error={errors.countryCodeId}
                  sx={stylesAutoCompleteSingleSelect}
                  getOptionLabel={(option) => option.label}
                  value={countriesOptions.find((item) => item.value === prevValue) ?? null}
                  label={t('borderCrossingRORO.onBoarding.subscribe.form.countryCodeId.label')}
                  onChange={(_, { value }) => {
                    onChange?.(value);
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={9}>
            <TextField
              fullWidth
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.street}
              id={REGISTER_SUBSCRIBE_FORM.street}
              registration={register(REGISTER_SUBSCRIBE_FORM.street)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.street.label')}
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.postCode}
              id={REGISTER_SUBSCRIBE_FORM.postCode}
              registration={register(REGISTER_SUBSCRIBE_FORM.postCode)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.postCode.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.phoneNumber}
              id={REGISTER_SUBSCRIBE_FORM.phoneNumber}
              registration={register(REGISTER_SUBSCRIBE_FORM.phoneNumber)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.phoneNumber.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.companyEmail}
              id={REGISTER_SUBSCRIBE_FORM.companyEmail}
              registration={register(REGISTER_SUBSCRIBE_FORM.companyEmail)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.companyEmail.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.contactFirstName}
              id={REGISTER_SUBSCRIBE_FORM.contactFirstName}
              registration={register(REGISTER_SUBSCRIBE_FORM.contactFirstName)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.contactFirstName.label')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              labelSx={{ pb: 1 }}
              variant='outlined'
              error={errors.contactLastName}
              id={REGISTER_SUBSCRIBE_FORM.contactLastName}
              registration={register(REGISTER_SUBSCRIBE_FORM.contactLastName)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.contactLastName.label')}
            />
          </Grid>
        </Grid>
        <Typography component='div' variant='bodyMDMedium' sx={stylesSubscribeFormSubtitle}>
          {t('borderCrossingRORO.onBoarding.subscribe.form.loginInformation.label')}
        </Typography>
        <Grid container spacing={2} item xs={6}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              isRequired
              sx={{ mb: 0 }}
              id={REGISTER_SUBSCRIBE_FORM.email}
              variant='outlined'
              labelSx={{ pb: 1 }}
              error={errors.email}
              registration={register(REGISTER_SUBSCRIBE_FORM.email)}
              label={t('borderCrossingRORO.onBoarding.subscribe.form.email.label')}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name={REGISTER_SUBSCRIBE_FORM.termsAndContractValidated}
              render={({ fieldState: { invalid, error }, field: { onChange } }) => (
                <>
                  <FormControlLabel
                    required
                    control={<Checkbox onChange={onChange} />}
                    label={
                      <>
                        <Asterisk />
                        <Trans
                          i18nKey={t('borderCrossingRORO.onBoarding.subscribe.form.termsAndConditionOfService.label')}
                          components={{
                            span: (
                              <Link
                                href={`${process.env.PUBLIC_URL}/files/termsAndConditions.pdf`}
                                download='T&C.pdf'
                              />
                            ),
                          }}
                        />
                      </>
                    }
                  />
                  {invalid && <FormHelperText sx={{ color: '#F1416C' }}>{error?.message}</FormHelperText>}
                </>
              )}
            />
          </Grid>
        </Grid>
        {isSubscribeFormInError && (
          <SnakebarToaster
            isOpen
            severity='error'
            content={t('borderCrossingRORO.onBoarding.subscribe.error.toasterMessage', {
              supportEmail: process.env.REACT_APP_SUPPORT_EMAIL,
            })}
            onClose={handleCloseToaster}
            autoHideDuration={null}
          />
        )}
      </CardContent>
      <CardActions sx={{ pb: 3, width: '50%' }}>
        <Button variant='contained' color='inherit' sx={{ mr: 'auto' }} onClick={handleCancelTheRequest}>
          {t('borderCrossingRORO.onBoarding.subscribe.form.cancelButton.label')}
        </Button>
        <Button
          startIcon={<SendPlaneLineIcon />}
          color='success'
          variant='contained'
          disabled={isSubscribePending}
          onClick={handleSubmitSubscribe}
        >
          {t('borderCrossingRORO.onBoarding.subscribe.form.subscribe.label')}
        </Button>
      </CardActions>
    </Card>
  );
};
